"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Award, HardHat, CircleCheck as CheckCircle2, ArrowRight, UserCheck, Building2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const accreditations = [{
  name: "NASC",
  title: "National Access & Scaffolding Confederation",
  description: "Audited safety procedures, technical compliance, and strict industry work standards."
}, {
  name: "CHAS",
  title: "Contractors Health and Safety Assessment Scheme",
  description: "Verified health and safety risk mitigation for contractors and developers."
}, {
  name: "Constructionline",
  title: "Pre-Qualified Procurement",
  description: "Streamlined compliance checks and vetted operational capability."
}];
const leadershipPoints = [{
  title: "Direct site management",
  description: "Leadership remains on site to inspect setups, oversee complex cantilevers, and supervise handover certificates."
}, {
  title: "Family-run accountability",
  description: "We take personal responsibility for project schedules, clear lines of communication, and safe dismantling."
}, {
  title: "Carrying forward our heritage",
  description: "Growing BMF while honouring Adam Story's legacy of authentic care, honest pricing, and steadfast reliability."
}];
const sitePhotoQuery = new WP_Query({
  post_type: "site_photo",
  per_page: 20
});
function PeteWoodImageConsumer() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="w-full h-full bg-muted animate-pulse" />;
  }
  const photo = posts.find(p => p.title === "Demolition Contractors Pete Wood Image" || p.customFields?.photo_name === "Demolition Contractors Pete Wood Image");
  const dynamicSrc = photo?.customFields?.image || photo?.featuredImage;
  const dynamicAlt = photo?.customFields?.alt_text || photo?.title || "Pete Wood, Lead Director at BMF Scaffolding Ltd";
  const imgData = dynamicSrc ? {
    src: dynamicSrc,
    srcset: "",
    sizes: "",
    alt: dynamicAlt
  } : {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/pete_wood_leadership.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/pete_wood_leadership.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/pete_wood_leadership.webp-wvc-sizes",
    alt: "Pete Wood, Lead Director at BMF Scaffolding Ltd"
  };
  return <img src={imgData.src} data-wvc-srcset={imgData.srcset} data-wvc-sizes={imgData.sizes} alt={imgData.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />;
}
export default function Leadership() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="aboutleadershiptrust" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Left Column: Pete Wood & Leadership Story */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="px-3 py-1 font-medium border-border text-muted-foreground">
                Leadership & Safety
              </Badge>
              <Badge variant="default" className="px-3 py-1 bg-primary text-primary-foreground">
                20+ Years Experience
              </Badge>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
              Direct site leadership you can hold accountable
            </h2>

            <p className="text-lg text-muted-foreground leading-relaxed">
              Led by Pete Wood, BMF Scaffolding Ltd pairs over two decades of practical trade experience with the genuine care of a family-run business. We do not pass off projects to unvetted subcontractors — Pete and senior supervisors manage our scaffolding directly on site across the South East.
            </p>

            {/* Pete Wood Profile Box */}
            <div className="rounded-xl border border-border bg-card p-6 shadow-sm flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <div className="relative shrink-0 overflow-hidden rounded-lg border border-border w-20 h-20 sm:w-24 sm:h-24 bg-muted">
                <PostsProvider wp_query={{
                post_type: "site_photo",
                per_page: 20,
                status: ["publish"]
              }}>
                  <PeteWoodImageConsumer />
                </PostsProvider>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <h3 className="font-default font-semibold text-lg text-card-foreground">
                    Pete Wood
                  </h3>
                  <UserCheck className="size-4 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Director & Senior Operations Lead
                </p>
                <p className="text-sm text-card-foreground mt-2 leading-snug">
                  Personally overseeing site health, safety compliance, scaffold designs, and transparent client reporting.
                </p>
              </div>
            </div>

            {/* Core Values / Leadership Pillars */}
            <div className="grid grid-cols-1 gap-4 pt-2">
              {leadershipPoints.map((point, i) => <div key={point.title} data-index={i} className="flex items-start gap-4 rounded-lg bg-muted/40 p-4 border border-border/60">
                  <div className="mt-1 flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <CheckCircle2 className="size-5" />
                  </div>
                  <div>
                    <h4 className="font-default font-semibold text-foreground text-base">
                      {point.title}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">
                      {point.description}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* CTA action row */}
            <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button asChild size="lg" className="font-medium">
                <Link to="/ContactRequestQuotePage">
                  Request a site quote
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="font-medium">
                <Link to="/HealthSafetyAccreditationsPage">
                  View safety accreditations
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Accreditations & Rigour */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.15,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col gap-6">
            <Card className="border border-border bg-card shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 text-primary mb-1">
                  <ShieldCheck className="size-6" />
                  <span className="text-xs font-semibold uppercase tracking-wider">
                    Verified Compliance
                  </span>
                </div>
                <CardTitle className="text-2xl font-default font-bold text-card-foreground">
                  Industry accreditations
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Rigorous independent safety and operational standards confirmed across all contracts.
                </p>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                {accreditations.map((item, i) => <div key={item.name} data-index={i} className="p-4 rounded-lg bg-background border border-border flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <span className="font-default font-bold text-foreground text-base tracking-wide">
                        {item.name}
                      </span>
                      <Award className="size-4 text-primary" />
                    </div>
                    <span className="text-xs font-semibold text-muted-foreground">
                      {item.title}
                    </span>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {item.description}
                    </p>
                  </div>)}

                <Separator className="my-2" />

                {/* Direct Site Guarantee Note */}
                <div className="rounded-lg bg-muted/50 p-4 border border-border text-xs text-muted-foreground leading-relaxed flex items-start gap-3">
                  <HardHat className="size-5 shrink-0 text-primary mt-0.5" />
                  <div>
                    <strong className="text-foreground block font-medium mb-0.5">
                      Commercial & domestic competence
                    </strong>
                    Serving main contractors, solar installers, roofing teams, councils, and homeowners across Berkshire, Surrey, Hampshire, Buckinghamshire, and London.
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Regional Operations Box */}
            <div className="rounded-xl border border-border bg-card p-6 shadow-sm flex items-start gap-4">
              <div className="p-3 rounded-md bg-primary/10 text-primary shrink-0">
                <Building2 className="size-5" />
              </div>
              <div className="flex flex-col gap-1">
                <h4 className="font-default font-semibold text-sm text-card-foreground">
                  Coverage throughout South East
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Fast response times and direct supervisor check-ins for scheduled commercial works and responsive site fixes.
                </p>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}